import React, { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Download, Loader2, Lock, RefreshCw, ShieldAlert } from "lucide-react";

/**
 * Admin enquiries page.
 *
 * NOTE: Enquiries have Public INSERT + no public SELECT via RLS.
 * To view them, the admin enters a Supabase service_role key (kept only in memory).
 * This is a simple built-in admin that avoids building a full auth system.
 *
 * A more secure long-term approach would be to enable Supabase Auth and protect
 * a SELECT policy with auth.uid() checks. For now, this is a pragmatic MVP.
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

export default function AdminEnquiriesPage() {
  const [key, setKey] = useState(() => sessionStorage.getItem("gx_admin_key") || "");
  const [client, setClient] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!client) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const connect = (e) => {
    e?.preventDefault();
    if (!key || key.length < 20) {
      setError("Please paste a valid Supabase service_role key.");
      return;
    }
    sessionStorage.setItem("gx_admin_key", key);
    setError("");
    const c = createClient(SUPABASE_URL, key, { auth: { persistSession: false } });
    setClient(c);
  };

  const disconnect = () => {
    sessionStorage.removeItem("gx_admin_key");
    setKey("");
    setClient(null);
    setEnquiries([]);
  };

  const load = async () => {
    if (!client) return;
    setLoading(true);
    setError("");
    try {
      const { data, error } = await client
        .from("enquiries")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      setEnquiries(data || []);
    } catch (e) {
      setError(e.message || "Failed to load enquiries");
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (enquiries.length === 0) return;
    const cols = [
      "created_at",
      "name",
      "company",
      "email",
      "phone",
      "requirement",
      "product_name",
      "product_slug",
      "source",
      "status",
      "id",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v).replace(/"/g, '""');
      return `"${s}"`;
    };
    const header = cols.join(",");
    const rows = enquiries.map((e) => cols.map((c) => esc(e[c])).join(","));
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `giftxpert-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!client) {
    return (
      <div className="gx-container py-20 min-h-[60vh]" data-testid="admin-login">
        <div className="max-w-lg mx-auto bg-white border border-slate-200 p-8">
          <div className="w-12 h-12 bg-slate-900 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-gold" />
          </div>
          <h1 className="font-outfit text-2xl font-medium mb-2">Admin — Enquiries</h1>
          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            Paste your Supabase <strong>service_role</strong> key to view and export enquiries.
            The key is kept only in your browser's session storage.
          </p>
          <form onSubmit={connect} className="space-y-4">
            <div>
              <Label htmlFor="srv-key" className="text-xs uppercase tracking-wider text-slate-500">
                service_role key
              </Label>
              <Input
                id="srv-key"
                data-testid="admin-service-key"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="eyJhbGciOi..."
                className="h-11 mt-2 font-mono text-xs"
              />
            </div>
            {error && (
              <div className="flex gap-2 items-start text-sm text-red-600" data-testid="admin-error">
                <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {error}
              </div>
            )}
            <button type="submit" data-testid="admin-login-btn" className="btn-primary w-full">
              Connect
            </button>
          </form>
          <p className="mt-6 text-[11px] text-slate-400">
            Find the key in your Supabase dashboard → Project Settings → API → service_role.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="gx-container py-12 md:py-16 min-h-[60vh]" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <div className="gx-overline mb-2">Admin</div>
          <h1 className="font-outfit text-3xl md:text-4xl font-light tracking-tight">
            Enquiries <span className="text-slate-400 text-2xl">({enquiries.length})</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            data-testid="admin-refresh"
            disabled={loading}
            className="btn-secondary"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            onClick={downloadCSV}
            data-testid="admin-download-csv"
            disabled={enquiries.length === 0}
            className="btn-primary disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={disconnect} className="btn-secondary" data-testid="admin-disconnect">
            Disconnect
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-4 mb-6" data-testid="admin-error">
          {error}
        </div>
      )}

      {loading && enquiries.length === 0 ? (
        <div className="text-slate-500 py-12 text-center">Loading enquiries...</div>
      ) : enquiries.length === 0 ? (
        <div className="text-slate-500 py-12 text-center border border-dashed border-slate-200">
          No enquiries yet. Once customers start submitting the contact form, they will appear here.
        </div>
      ) : (
        <div className="overflow-x-auto border border-slate-200" data-testid="admin-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-950 text-white">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Company</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Phone</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Requirement</th>
              </tr>
            </thead>
            <tbody>
              {enquiries.map((e, i) => (
                <tr key={e.id} className={i % 2 ? "bg-slate-50" : "bg-white"} data-testid={`enquiry-row-${i}`}>
                  <td className="px-4 py-3 whitespace-nowrap text-slate-500 text-xs">
                    {new Date(e.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-medium">{e.name}</td>
                  <td className="px-4 py-3">{e.company}</td>
                  <td className="px-4 py-3 text-xs">
                    <a href={`mailto:${e.email}`} className="text-gold hover:underline">{e.email}</a>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <a href={`tel:${e.phone}`} className="hover:underline">{e.phone}</a>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{e.product_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-md">{e.requirement}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
